/**
 * Fix GD (2026-10-14): "Para la liquidación del CEN Operativo hace falta una matriz de prorrateo:
 * una columna de referencia con el % de ingresos de cada obra y otra editable con el % de participación
 * del trabajador en cada obra (por defecto 50/50 si está en dos obras; la Directiva puede modificarlo,
 * por ejemplo 70/30). Se valida la distribución, se procesa la OP y, una vez aprobada, se imputa el
 * salario a las obras según el porcentaje validado" (CEN).
 */
import { useState } from "react";
import { CheckCircle2, Check } from "lucide-react";
import { formatPYG } from "../../utils/format";
import { pctFmt, leyendaIngresos } from "./IngresosObras";

// Quienes validan la distribución (el backend repite la misma regla).
export const PRORRATEO_ROLES = ["Superadministrador", "Director", "Gerencia Financiera", "Gerente Tecnico", "Gerencia Técnica"];

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

// Reparte el jornal bruto según los % (los de 0 % no participan; la última obra absorbe el redondeo).
export function repartir(bruto, pcts) {
  const out = pcts.map(() => 0);
  const idx = pcts.map((p, i) => (num(p) > 0 ? i : -1)).filter(i => i >= 0);
  let acum = 0;
  idx.forEach((i, k) => {
    out[i] = k === idx.length - 1 ? Math.round(bruto - acum) : Math.round(bruto * num(pcts[i]) / 100);
    acum += out[i];
  });
  return out;
}

export function useMatrizProrrateo() {
  const [obrasSel, setObrasSel] = useState([]);
  const [editadas, setEditadas] = useState({});   // worker_id -> { obra_id: % } de las filas que se tocaron
  const [validada, setValidada] = useState(false);

  // Sin tocar, el costo se reparte en partes iguales entre las obras elegidas (dos obras: 50/50).
  const pctDe = (wid, oid) => {
    if (editadas[wid]) return editadas[wid][oid] ?? 0;
    const i = obrasSel.indexOf(oid);
    if (i < 0) return 0;
    const n = obrasSel.length;
    const base = Math.floor(10000 / n) / 100;
    return i === n - 1 ? Math.round((100 - base * (n - 1)) * 100) / 100 : base;
  };
  const totalFila = (wid) => Math.round(obrasSel.reduce((s, oid) => s + num(pctDe(wid, oid)), 0) * 100) / 100;
  const filaOk = (wid) => Math.abs(totalFila(wid) - 100) <= 0.01;
  const partesDe = (wid) => obrasSel.map(oid => ({ obra_id: oid, pct: num(pctDe(wid, oid)) }));

  const cambiarObras = (lista) => { setObrasSel(lista); setValidada(false); };
  const setPct = (wid, oid, valor) => {
    setEditadas(prev => ({ ...prev, [wid]: { ...(prev[wid] || Object.fromEntries(obrasSel.map(o => [o, pctDe(wid, o)]))), [oid]: valor } }));
    setValidada(false);
  };
  const igualar = () => { setEditadas({}); setValidada(false); };

  return { obrasSel, cambiarObras, pctDe, setPct, totalFila, filaOk, partesDe, igualar, validada, setValidada };
}

export default function MatrizProrrateo({ m, filas, ingresos, puedeValidar }) {
  const obras = (ingresos && ingresos.obras) || [];
  const sel = m.obrasSel.map(id => obras.find(o => o.obra_id === id)).filter(Boolean);
  const todasOk = sel.length > 0 && filas.every(f => m.filaOk(f.worker_id));
  const alternar = (id) => m.cambiarObras(m.obrasSel.includes(id) ? m.obrasSel.filter(x => x !== id) : [...m.obrasSel, id]);
  const montos = (f) => repartir(f.bruto || 0, sel.map(o => m.pctDe(f.worker_id, o.obra_id)));
  const totalObra = (k) => filas.reduce((s, f) => s + montos(f)[k], 0);

  return (
    <div className="bg-white rounded-xl border border-violet-200 shadow-sm overflow-hidden" data-testid="liq-matriz">
      <div className="px-4 py-3 bg-violet-50 border-b border-violet-100">
        <p className="text-sm font-bold text-violet-900">Prorrateo CEN Operativo</p>
        <p className="text-[11px] text-violet-800/80 mt-0.5">
          Elegí las obras entre las que se reparte el jornal y el % de cada trabajador en cada una (sin tocar, partes iguales). La distribución la valida la Directiva;
          cuando Tesorería aprueba la OP, el jornal se imputa a cada obra con ese porcentaje.
        </p>
      </div>

      <div className="px-4 py-3 space-y-2 border-b border-gray-100">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
          Obras · referencia: % de ingresos <span className="font-normal normal-case text-gray-400">({ingresos ? leyendaIngresos(ingresos.base) : "cargando…"})</span>
        </p>
        <div className="flex flex-wrap gap-2">
          {obras.map(o => {
            const on = m.obrasSel.includes(o.obra_id);
            return (
              <button type="button" key={o.obra_id} onClick={() => alternar(o.obra_id)} disabled={!puedeValidar}
                className={`flex items-center gap-1.5 border rounded-lg px-2.5 py-1 text-xs disabled:opacity-60 ${on ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-gray-300 text-gray-700 hover:border-violet-400"}`}
                data-testid={`pr-obra-${o.obra_id}`}>
                {on && <Check size={12} />}
                {o.nombre}
                <span className={`tabular-nums font-semibold ${on ? "text-violet-100" : "text-sky-700"}`}>{pctFmt(o.ingresos_pct)} %</span>
              </button>
            );
          })}
          {obras.length === 0 && <p className="text-xs text-gray-400">No hay obras activas.</p>}
        </div>
      </div>

      {sel.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-gray-500" data-testid="pr-sin-obras">Elegí al menos una obra para armar la matriz.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="pr-tabla">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Trabajador</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">Jornales</th>
                {sel.map(o => (
                  <th key={o.obra_id} className="px-3 py-2 text-center font-semibold text-gray-700 min-w-[110px]" data-testid={`pr-th-${o.obra_id}`}>
                    <span className="block truncate max-w-[160px] mx-auto">{o.nombre}</span>
                    <span className="block text-[10px] font-normal text-sky-700">ingresos {pctFmt(o.ingresos_pct)} %</span>
                  </th>
                ))}
                <th className="px-3 py-2 text-center font-semibold text-gray-500 uppercase tracking-wide">Total %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filas.map((f, i) => {
                const mt = montos(f);
                const tot = m.totalFila(f.worker_id);
                return (
                  <tr key={f.worker_id} data-testid={`pr-fila-${i}`}>
                    <td className="px-3 py-2 font-semibold text-gray-900">{f.worker_name}<span className="block text-[10px] font-normal text-gray-400">{f.role_title || ""}</span></td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatPYG(f.bruto || 0)}</td>
                    {sel.map((o, k) => (
                      <td key={o.obra_id} className="px-3 py-2 text-center">
                        <input type="number" min="0" max="100" step="0.01" value={m.pctDe(f.worker_id, o.obra_id)} disabled={!puedeValidar}
                          onChange={e => m.setPct(f.worker_id, o.obra_id, e.target.value)}
                          className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-xs text-right tabular-nums focus:ring-2 focus:ring-violet-300 disabled:bg-gray-50"
                          data-testid={`pr-pct-${i}-${k}`} />
                        <span className="block text-[10px] text-gray-400 tabular-nums mt-0.5" data-testid={`pr-monto-${i}-${k}`}>{formatPYG(mt[k])}</span>
                      </td>
                    ))}
                    <td className={`px-3 py-2 text-center font-bold tabular-nums ${m.filaOk(f.worker_id) ? "text-emerald-700" : "text-red-600"}`} data-testid={`pr-total-${i}`}>
                      {pctFmt(tot)} %
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200 font-bold">
                <td className="px-3 py-2 text-gray-600" colSpan={2}>Se imputa a cada obra</td>
                {sel.map((o, k) => <td key={o.obra_id} className="px-3 py-2 text-center tabular-nums text-gray-800" data-testid={`pr-suma-${k}`}>{formatPYG(totalObra(k))}</td>)}
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="px-4 py-3 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center gap-2">
        {!puedeValidar && (
          <p className="text-[11px] text-amber-700 flex-1" data-testid="pr-solo-directiva">La distribución entre obras la define y valida la Directiva (Dirección, Gerencia Financiera o Gerencia Técnica).</p>
        )}
        {puedeValidar && m.validada && (
          <p className="text-xs font-semibold text-emerald-700 flex-1 flex items-center gap-1.5" data-testid="pr-validada"><CheckCircle2 size={14} /> Distribución validada. Ya se pueden generar las OP; si cambiás algo, hay que validar de nuevo.</p>
        )}
        {puedeValidar && !m.validada && (
          <p className="text-[11px] text-gray-500 flex-1" data-testid="pr-sin-validar">{todasOk ? "Revisá los porcentajes y validá la distribución." : "Cada trabajador tiene que sumar 100 %."}</p>
        )}
        {puedeValidar && (
          <div className="flex gap-2">
            <button type="button" onClick={m.igualar} className="px-3 py-1.5 text-xs font-semibold border border-gray-300 rounded-lg hover:bg-gray-50" data-testid="pr-igualar-btn">Partes iguales</button>
            <button type="button" onClick={() => m.setValidada(true)} disabled={!todasOk || m.validada}
              className="px-3 py-1.5 text-xs font-bold bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white rounded-lg flex items-center gap-1.5" data-testid="pr-validar-btn">
              <CheckCircle2 size={13} /> Validar distribución
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
